// Social (Twetch) flows in the wallet telemetry stream, so #errors and the
// wallet-health panel see sign-ins and posts the way they see listings and
// purchases: which wallet, which step, which server code, what it said.
//
// Nothing here can throw into the composer: every call is wrapped.

import Wallet from '@/utils/wallet'
import {
	beginWalletFlow,
	finishWalletFlow,
	providerSlug,
	classifyWalletError,
	type WalletFlow
} from '@/utils/wallet/telemetry'
import type { WalletTelemetryEvent, WalletTelemetryStage } from '@/utils/wallet/telemetry-schema'

function provider() {
	try {
		return providerSlug(String(Wallet.type() || 'unknown'))
	} catch {
		return providerSlug('unknown')
	}
}

/// Route-safe detail text: the server's sentence, the address kind and the
/// step, in the character set the telemetry route accepts.
function detail(parts: Array<string | null | undefined>): string {
	return parts
		.filter((p): p is string => !!p)
		.join(' · ')
		.replace(/[^A-Za-z0-9_.,:;=()\[\]/ '"·—-]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 200)
}

function addressKind(address: string | null | undefined): string | null {
	if (!address) return 'addr=none'
	const a = address.toLowerCase()
	if (a.startsWith('bc1p')) return `addr=bc1p…${a.slice(-4)}`
	if (a.startsWith('bc1q')) return `addr=bc1q…${a.slice(-4)}`
	if (a.startsWith('3')) return `addr=3…${a.slice(-4)}`
	return `addr=${a.slice(0, 2)}…${a.slice(-4)}`
}

export function beginSocialFlow(
	event: Extract<WalletTelemetryEvent, 'social_login' | 'social_post' | 'social_action'>,
	stage: WalletTelemetryStage
): WalletFlow | null {
	try {
		return beginWalletFlow(event, provider(), 'social_composer', stage)
	} catch {
		return null
	}
}

export function socialFlowSucceeded(flow: WalletFlow | null, stage: WalletTelemetryStage): void {
	if (!flow) return
	try {
		finishWalletFlow(flow, stage, 'success')
	} catch {}
}

/// `kind` is the composer's own classification (identity-mismatch, expired,
/// replay, …) and becomes `code=social_<kind>`; the server's sentence rides
/// in `detail=` next to the address kind and whether it was a thesis.
export function socialFlowFailed(
	flow: WalletFlow | null,
	stage: WalletTelemetryStage,
	error: unknown,
	kind: string,
	context: { address?: string | null; thesis?: boolean; cancelled?: boolean } = {}
): void {
	if (!flow) return
	try {
		const message = String((error as { message?: unknown })?.message ?? error ?? '')
		const status = (error as { status?: unknown })?.status
		finishWalletFlow(flow, stage, context.cancelled ? 'cancelled' : 'failure', {
			error_category: context.cancelled ? 'user_rejected' : classifyWalletError(error),
			provider_error_code: `social_${kind}`.replace(/[^a-z0-9_]/g, '_').slice(0, 32),
			error_detail: detail([
				context.thesis ? 'thesis' : null,
				addressKind(context.address),
				typeof status === 'number' ? `http=${status}` : null,
				message
			])
		})
	} catch {}
}
